import { css } from "@emotion/css";
import { useCurrentScrollArea } from "@octopusdeploy/design-system-components";
import type { PropsWithChildren } from "react";
import React, { useLayoutEffect, useRef, useState, useCallback } from "react";
import { useStickyPageHeaderHeight } from "~/areas/tasks/components/Task/Kubernetes/Manifests/useStickyPageHeaderHeight";
const styles = {
    stickyContainer: (pageHeaderHeight: string) => css({
        position: "sticky",
        top: pageHeaderHeight,
        overflowY: "auto",
    }),
};
type StickyResizingContainerProps = PropsWithChildren<{
    resizeToken: unknown;
}>;
export const StickyResizingContainer = (props: StickyResizingContainerProps) => {
    const [originalFullHeight, setOriginalFullHeight] = useState<number | undefined>();
    const activeScrollArea = useCurrentScrollArea();
    const stickyContainerRef = useRef<HTMLDivElement>(null);
    //For the scrollMarginTop to work, this needs to match the height of the page header
    const pageHeaderHeight = useStickyPageHeaderHeight();
    const resizeTargetDetails = useCallback(() => {
        if (!stickyContainerRef.current) {
            return;
        }
        //we need to use the original height of the sticky container so we are correctly resizing it based on its original height
        const detailsHeight = originalFullHeight || stickyContainerRef.current.offsetHeight;
        const scrollAreaBoundingRect = activeScrollArea.getBoundingClientRect();
        const scrollAreaHeight = activeScrollArea.clientHeight;
        const boundingRect = stickyContainerRef.current.getBoundingClientRect();
        //the top needs to account for the scroll area not being at the top
        const top = boundingRect.top - scrollAreaBoundingRect.top;
        const availableHeight = scrollAreaHeight - top;
        const height = Math.min(detailsHeight, availableHeight);
        //only if we actually have a valid height do we set the height
        if (height > 0) {
            stickyContainerRef.current.style.height = `${height}px`;
        }
    }, [activeScrollArea, originalFullHeight]);
    useLayoutEffect(() => {
        setOriginalFullHeight(undefined);
        stickyContainerRef.current?.style.removeProperty("height");
    }, [props.resizeToken]);
    useLayoutEffect(() => {
        if (!stickyContainerRef.current) {
            return;
        }
        const resizeObserver = new ResizeObserver((entries) => {
            //if the sticky container is resizing but the full height is still unknown,
            if (!originalFullHeight && stickyContainerRef.current) {
                setOriginalFullHeight(stickyContainerRef.current.offsetHeight);
                resizeTargetDetails();
            }
        });
        //store the current original height
        if (!originalFullHeight && stickyContainerRef.current.offsetHeight > 0) {
            setOriginalFullHeight(stickyContainerRef.current.offsetHeight);
        }
        //resize the container div
        resizeTargetDetails();
        //Attach the event handlers
        activeScrollArea.addEventListener("scroll", resizeTargetDetails);
        window.addEventListener("resize", resizeTargetDetails);
        resizeObserver.observe(stickyContainerRef.current);
        return () => {
            window.removeEventListener("resize", resizeTargetDetails);
            activeScrollArea.removeEventListener("scroll", resizeTargetDetails);
            resizeObserver.disconnect();
        };
    }, [activeScrollArea, originalFullHeight, resizeTargetDetails]);
    return (<div className={styles.stickyContainer(pageHeaderHeight)} ref={stickyContainerRef}>
            {props.children}
        </div>);
};
